'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { Instructor, InstructorDetails } from '@udemy-v2/browse-course';
import { useFetchCoursesByInstructors } from '@/lib/hooks/use-fetch-courses-by-instructors';
import { TrackableDiscoveryItem } from '@udemy-v2/react-browse-event-tracking';
import { useClpPurchaseContext } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { TaughtCoursesRequestConfig } from '@/types/taught-courses-api';
import { BrowseCourseWithInstructorDetails } from '@/types/course';
import { useClpCourse } from './clp-course-provider.react-component';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
export interface CourseWithInstructorDetailsContextType {
  /** Instructor details for the course instructors */
  instructorDetails: InstructorDetails[];
  /** More courses by this course's instructor which also include instructor details */
  moreCoursesByInstructor: Record<string, TrackableDiscoveryItem<BrowseCourseWithInstructorDetails>[]>;
  isLoading: boolean;
  error: string | null;
}

// Simple type alias to specify that the instructor has a non-null url
type InstructorWithUrl = Instructor & {
  url: string;
};

/**
 * Context for the course with instructor details.
 */
export const CourseWithInstructorDetailsContext = createContext<CourseWithInstructorDetailsContextType | undefined>(undefined);
export interface CourseWithInstructorDetailsProviderProps {
  children: ReactNode;
  instructors?: Instructor[];
  pageSize?: number;
}
export const CourseWithInstructorDetailsProvider = t0 => {
  const $ = _c(31);
  const {
    children,
    instructors: t1,
    pageSize: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const instructors = t3;
  const pageSize = t2 === undefined ? 4 : t2;
  const currentCourse = useClpCourse();
  const {
    isPersonalPlanSubscriber
  } = useClpPurchaseContext();
  const hasOrganization = useUserHasOrganization();
  let t4;
  if ($[2] !== instructors) {
    t4 = instructors?.filter(_temp);
    $[2] = instructors;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const instructorsWithUrls = t4 as InstructorWithUrl[];
  let t5;
  if ($[4] !== hasOrganization || $[5] !== instructorsWithUrls || $[6] !== isPersonalPlanSubscriber || $[7] !== pageSize) {
    let t6;
    if ($[9] !== hasOrganization || $[10] !== isPersonalPlanSubscriber || $[11] !== pageSize) {
      t6 = instructor_0 => ({
        userId: instructor_0.id,
        instructorUrl: instructor_0.url,
        pageSize,
        isInUserSubscription: isPersonalPlanSubscriber,
        organizationCoursesOnly: hasOrganization
      });
      $[9] = hasOrganization;
      $[10] = isPersonalPlanSubscriber;
      $[11] = pageSize;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = instructorsWithUrls.map(t6);
    $[4] = hasOrganization;
    $[5] = instructorsWithUrls;
    $[6] = isPersonalPlanSubscriber;
    $[7] = pageSize;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const instructorConfigs = t5;
  const {
    coursesByInstructorsByUrl,
    instructorDetailsByUrl,
    isLoading,
    error
  } = useFetchCoursesByInstructors(instructorConfigs);
  let t6;
  if ($[13] !== instructorDetailsByUrl || $[14] !== instructorsWithUrls) {
    let t7;
    if ($[16] !== instructorDetailsByUrl) {
      t7 = instructor_1 => {
        const details = instructorDetailsByUrl[instructor_1.url];
        if (!details) {
          return;
        }
        return {
          ...details,
          id: instructor_1.id
        };
      };
      $[16] = instructorDetailsByUrl;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = instructorsWithUrls.map(t7).filter(_temp2);
    $[13] = instructorDetailsByUrl;
    $[14] = instructorsWithUrls;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const instructorDetails = t6;
  let filtered;
  if ($[18] !== coursesByInstructorsByUrl || $[19] !== currentCourse.id) {
    filtered = {};
    Object.entries(coursesByInstructorsByUrl).forEach(t7 => {
      const [instructorUrl, courses] = t7;
      filtered[instructorUrl] = courses.filter(course => course.id !== currentCourse.id.toString());
    });
    $[18] = coursesByInstructorsByUrl;
    $[19] = currentCourse.id;
    $[20] = filtered;
  } else {
    filtered = $[20];
  }
  const filteredMoreCoursesByInstructor = filtered;
  let t7;
  if ($[21] !== error?.message || $[22] !== filteredMoreCoursesByInstructor || $[23] !== instructorDetails || $[24] !== isLoading) {
    t7 = () => ({
      instructorDetails,
      moreCoursesByInstructor: filteredMoreCoursesByInstructor,
      isLoading,
      error: error?.message ?? null
    });
    $[21] = error?.message;
    $[22] = filteredMoreCoursesByInstructor;
    $[23] = instructorDetails;
    $[24] = isLoading;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  error?.message;
  let t8;
  if ($[26] !== t7) {
    t8 = t7();
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const contextValue = t8;
  let t9;
  if ($[28] !== children || $[29] !== contextValue) {
    t9 = <CourseWithInstructorDetailsContext.Provider value={contextValue}>{children}</CourseWithInstructorDetailsContext.Provider>;
    $[28] = children;
    $[29] = contextValue;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};
export const useCourseWithInstructorDetailsContext = () => {
  const context = useContext(CourseWithInstructorDetailsContext);
  if (context === undefined) {
    throw new Error("useCourseWithInstructorDetailsContext must be used within a CourseWithInstructorDetailsProvider");
  }
  return context;
};
function _temp(instructor) {
  return instructor.url;
}
function _temp2(detail) {
  return !!detail;
}